import { useDirect130Host } from "@/app/_module/utils/direct130HostContext";
import { Search, X, Download, RefreshCw, Calendar as CalendarIcon, ChevronRight, ChevronDown, Building2, Check, Globe, Users, Trophy, Loader2, Eye, Share2 } from "lucide-react";
import { useState, useEffect, useMemo, useCallback } from "react";
import { DateRange } from "react-day-picker";
import { format, subDays, startOfMonth, endOfMonth, subMonths, startOfYear, startOfWeek, endOfWeek, differenceInDays, previousThursday, nextWednesday, addDays, isBefore, isAfter, getDay } from "date-fns";

import { Button } from "@/app/_module/components/ui/button";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import { Input } from "@/app/_module/components/ui/input";
import { Calendar } from "@/app/_module/components/ui/calendar";
import { Badge } from "@/app/_module/components/ui/badge";
import { Switch } from "@/app/_module/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/app/_module/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/_module/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { useToast } from "@/app/_module/components/ui/use-toast";
import { cn } from "@/app/_module/lib/utils";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { isDemoMode } from "@/app/_module/utils/demoMode";

import type { AgencyTreeNode } from "../hooks/use-agency-tree";

interface AgencyAgent {
  id: string;
  name: string | null;
  email?: string | null;
  showOnLeaderboard?: boolean;
}
type ScopeMode = "agency_only" | "agency_wide" | "custom" | "parent_godmode";
// UI state. Derived from real backend fields:
//   agency_wide_enabled (column)                       — Agency-Wide Mode toggle
//   features.leaderboard_scope.customAgencyIds         — whole-agency picks
//   features.leaderboard_scope.customUserIds           — specific-agent picks
//   parent_godmode_enabled (column)                    — privileged "see all below" mode
//
// `parent_godmode` is a CLIENT-SIDE-ONLY mode — it doesn't persist a new
// backend setting on its own. The toggle in Settings determines whether
// god-mode is AVAILABLE; selecting it here just sets a session preference
// that propagates `?godmode=true` on the leaderboard API call.
interface LeaderboardScope {
  mode: ScopeMode;
  customAgencyIds: string[];
  customUserIds: string[];
}
function uiModeToBackendBody(scope: LeaderboardScope): {
  agencyWideEnabled: boolean;
  leaderboardScope: { customAgencyIds: string[]; customUserIds: string[] };
} {
  if (scope.mode === "agency_only") {
    return { agencyWideEnabled: false, leaderboardScope: { customAgencyIds: [], customUserIds: [] } };
  }
  if (scope.mode === "agency_wide") {
    return { agencyWideEnabled: true, leaderboardScope: { customAgencyIds: [], customUserIds: [] } };
  }
  if (scope.mode === "parent_godmode") {
    // God-mode doesn't change the agency_wide_enabled setting — we leave
    // whatever was persisted intact so toggling out of god-mode falls back
    // to the user's prior agency-wide/agency-only state. Persist nothing.
    return { agencyWideEnabled: false, leaderboardScope: { customAgencyIds: [], customUserIds: [] } };
  }
  // custom
  return {
    agencyWideEnabled: true,
    leaderboardScope: {
      customAgencyIds: scope.customAgencyIds,
      customUserIds: scope.customUserIds,
    },
  };
}
function backendToUiMode(
  agencyWideEnabled: boolean,
  customAgencyIds: string[],
  customUserIds: string[],
): LeaderboardScope {
  if (!agencyWideEnabled) return { mode: "agency_only", customAgencyIds: [], customUserIds: [] };
  if (customAgencyIds.length > 0 || customUserIds.length > 0) {
    return { mode: "custom", customAgencyIds, customUserIds };
  }
  return { mode: "agency_wide", customAgencyIds: [], customUserIds: [] };
}

export interface CustomDateRange {
  from: Date;
  to: Date;
}

interface AdvancedLeaderboardFiltersProps {
  selectedPeriod: string;
  selectedMetric: string;
  searchQuery?: string; // Add this prop to keep in sync
  onPeriodChange: (period: string) => void;
  onMetricChange: (metric: string) => void;
  onSearch?: (query: string) => void;
  onRefresh?: () => void;
  loading?: boolean;
  leaderboardData?: any; // Add leaderboard data for export
  customDateRange?: CustomDateRange;
  onCustomDateChange?: (dateRange: CustomDateRange | undefined) => void;
  // Agency switcher props
  agencies?: AgencyTreeNode[];
  selectedAgencyId?: string;
  onAgencyChange?: (agencyId: string) => void;
  showAgencySwitcher?: boolean;
  // Scope picker (owner-only, persisted on the agency)
  isAgencyOwner?: boolean;
  ownerAgencyId?: string;
  ownerAgencyName?: string;
  onScopeSaved?: () => void;
  // Parent god-mode availability. When true the owner can flip into the
  // privileged "Full downline" view. Controlled by the Settings toggle —
  // the picker only EXPOSES the option when this is true.
  parentGodmodeAvailable?: boolean;
  // Session-only god-mode toggle. The leaderboard page reads this to pass
  // ?godmode=true on its API call and to render the "Owner view" badge.
  isGodmodeActive?: boolean;
  onGodmodeChange?: (active: boolean) => void;
  // Share-your-win entry point — rendered next to Export so both "take this
  // data with you" actions live in one place. Absent → button hidden.
  onShare?: () => void;
}

// Helper to get the most recent occurrence of a specific weekday (0=Sun, 1=Mon, ..., 6=Sat)
// If today is that weekday, returns today. Otherwise returns the most recent past occurrence.
const getMostRecentWeekday = (date: Date, targetDay: number): Date => {
  const currentDay = getDay(date);
  const diff = (currentDay - targetDay + 7) % 7;
  return diff === 0 ? date : subDays(date, diff);
};

// Day names for labels
const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const dayNamesFull = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Generate week ranges for a specific start day (e.g., Thu-Thu means Thu to following Wed, 7 days)
const getWeekRangeForDay = (today: Date, startDay: number) => {
  const thisWeekStart = getMostRecentWeekday(today, startDay);
  const thisWeekEnd = addDays(thisWeekStart, 6);
  const lastWeekStart = subDays(thisWeekStart, 7);
  const lastWeekEnd = subDays(thisWeekEnd, 7);

  const endDay = (startDay + 6) % 7; // The day before the start day
  const label = `${dayNames[startDay]}-${dayNames[endDay]}`;

  return {
    thisWeek: { label: `This Week (${label})`, from: thisWeekStart, to: thisWeekEnd },
    lastWeek: { label: `Last Week (${label})`, from: lastWeekStart, to: lastWeekEnd },
  };
};

// Preset range with optional group for UI organization
interface PresetRange {
  label: string;
  from: Date;
  to: Date;
  group?: 'quick' | 'this-week' | 'last-week' | 'monthly';
}

// Quick preset ranges for easy selection
const getPresetRanges = (): PresetRange[] => {
  const today = new Date();

  // Generate all weekday-to-weekday options
  const weekRanges = [
    getWeekRangeForDay(today, 0), // Sun-Sat
    getWeekRangeForDay(today, 1), // Mon-Sun
    getWeekRangeForDay(today, 2), // Tue-Mon
    getWeekRangeForDay(today, 3), // Wed-Tue
    getWeekRangeForDay(today, 4), // Thu-Wed
    getWeekRangeForDay(today, 5), // Fri-Thu
    getWeekRangeForDay(today, 6), // Sat-Fri
  ];

  return [
    // Quick selections
    { label: "Today", from: today, to: today, group: 'quick' },
    { label: "Yesterday", from: subDays(today, 1), to: subDays(today, 1), group: 'quick' },
    { label: "Last 7 Days", from: subDays(today, 6), to: today, group: 'quick' },
    { label: "Last 14 Days", from: subDays(today, 13), to: today, group: 'quick' },
    { label: "Last 30 Days", from: subDays(today, 29), to: today, group: 'quick' },
    // This Week options (all start days)
    ...weekRanges.map(r => ({ ...r.thisWeek, group: 'this-week' as const })),
    // Last Week options (all start days)
    ...weekRanges.map(r => ({ ...r.lastWeek, group: 'last-week' as const })),
    // Monthly
    { label: "This Month", from: startOfMonth(today), to: endOfMonth(today), group: 'monthly' },
    { label: "Last Month", from: startOfMonth(subMonths(today, 1)), to: endOfMonth(subMonths(today, 1)), group: 'monthly' },
    { label: "This Year", from: startOfYear(today), to: today, group: 'monthly' },
  ];
};

// Build a tree structure from the flat agency list
interface AgencyTreeItem {
  id: string;
  name: string;
  depth: number;
  children: AgencyTreeItem[];
}

function buildAgencyTree(agencies: AgencyTreeNode[]): AgencyTreeItem[] {
  if (!agencies || agencies.length === 0) return [];

  const nodeMap = new Map<string, AgencyTreeItem>();
  const roots: AgencyTreeItem[] = [];

  // Create nodes
  for (const agency of agencies) {
    nodeMap.set(agency.id, { id: agency.id, name: agency.name, depth: agency.depth, children: [] });
  }

  // Build parent-child relationships
  for (const agency of agencies) {
    const node = nodeMap.get(agency.id)!;
    if (agency.parentAgencyId && nodeMap.has(agency.parentAgencyId)) {
      nodeMap.get(agency.parentAgencyId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  return roots;
}

// Recursive tree node renderer
function AgencyTreeNode({
  node,
  selectedId,
  onSelect,
  expandedIds,
  onToggleExpand,
  depth = 0,
  customMode = false,
  customSelectedIds,
  onToggleCustom,
  rootAgencyId,
  agentsByAgency,
  loadingAgentsAgencyId,
  expandedAgentAgencies,
  onToggleAgentExpand,
  customSelectedUserIds,
  onToggleCustomUser,
}: {
  node: AgencyTreeItem;
  selectedId?: string;
  onSelect: (id: string) => void;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  depth?: number;
  // When customMode is true, clicking the row toggles inclusion in the custom
  // scope rather than acting as a single-select view filter. The root agency
  // is excluded from custom selection (it's effectively "Just my agency"+kids).
  customMode?: boolean;
  customSelectedIds?: string[];
  onToggleCustom?: (id: string) => void;
  rootAgencyId?: string;
  // Per-agent picking inside custom mode.
  agentsByAgency?: Record<string, AgencyAgent[]>;
  loadingAgentsAgencyId?: string | null;
  expandedAgentAgencies?: Set<string>;
  onToggleAgentExpand?: (agencyId: string) => void;
  customSelectedUserIds?: string[];
  onToggleCustomUser?: (userId: string) => void;
}) {
  const isSelected = selectedId === node.id;
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedIds.has(node.id);
  const isRoot = !!rootAgencyId && node.id === rootAgencyId;
  const isChecked = customMode && !!customSelectedIds?.includes(node.id);

  const handleRowClick = () => {
    if (customMode) {
      // Don't allow toggling the root agency in custom mode.
      if (isRoot) return;
      onToggleCustom?.(node.id);
    } else {
      onSelect(node.id);
    }
  };

  return (
    <div>
      <button
        type="button"
        className={cn(
          "flex items-center w-full text-left px-2 py-1.5 text-sm rounded-md transition-colors",
          customMode && isChecked
            ? "bg-amber-50 hover:bg-amber-100"
            : !customMode && isSelected
              ? "bg-primary/10 text-primary font-medium"
              : isRoot && customMode
                ? "opacity-50 cursor-not-allowed hover:bg-transparent"
                : "hover:bg-muted text-foreground",
        )}
        style={{ paddingLeft: `${8 + depth * 16}px` }}
        onClick={handleRowClick}
        disabled={customMode && isRoot}
      >
        {/* Expand/collapse toggle for parents */}
        {hasChildren ? (
          <span
            role="button"
            className="mr-1 p-0.5 rounded hover:bg-muted-foreground/10 flex-shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(node.id);
            }}
          >
            <ChevronRight
              className={cn(
                "h-3.5 w-3.5 text-muted-foreground transition-transform duration-200",
                isExpanded && "rotate-90"
              )}
            />
          </span>
        ) : (
          <span className="mr-1 w-[18px] flex-shrink-0" />
        )}
        {customMode && !isRoot && (
          // Fake checkbox — can't use the real <Checkbox> component here
          // because it renders a <button>, and this row is already a <button>.
          // Nested buttons are invalid HTML and break hydration.
          <span
            aria-hidden
            className={cn(
              "mr-2 h-3.5 w-3.5 flex-shrink-0 rounded-sm border flex items-center justify-center transition-colors",
              isChecked
                ? "bg-amber-600 border-amber-600 text-white"
                : "border-gray-300 bg-white",
            )}
          >
            {isChecked && <Check className="h-2.5 w-2.5" strokeWidth={3} />}
          </span>
        )}
        <Building2 className="h-3.5 w-3.5 mr-1.5 flex-shrink-0 text-muted-foreground" />
        <span className="truncate">{node.name}</span>
        {isRoot && customMode && (
          <Badge variant="secondary" className="ml-auto text-[10px] bg-gray-100 text-gray-500 font-normal">
            owner
          </Badge>
        )}
        {!customMode && isSelected && <Check className="h-3.5 w-3.5 ml-auto flex-shrink-0 text-primary" />}
        {customMode && isChecked && <Check className="h-3.5 w-3.5 ml-auto flex-shrink-0 text-amber-600" />}
      </button>

      {/* Per-agent expand button (custom mode, non-root, agency NOT whole-checked).
          Lazy-loads agents so the popover opens fast even for big trees. */}
      {customMode && !isRoot && !isChecked && onToggleAgentExpand && (
        (() => {
          const isAgentExpanded = expandedAgentAgencies?.has(node.id) ?? false;
          const isLoading = loadingAgentsAgencyId === node.id;
          const agents = agentsByAgency?.[node.id];
          const pickedHere = (customSelectedUserIds || []).filter((uid) =>
            (agents || []).some((a) => a.id === uid),
          ).length;
          return (
            <>
              <button
                type="button"
                className="flex items-center w-full text-left text-[10.5px] py-0.5 text-gray-500 hover:text-amber-700 transition-colors"
                style={{ paddingLeft: `${24 + depth * 16}px` }}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleAgentExpand(node.id);
                }}
              >
                <ChevronRight
                  className={cn(
                    "h-2.5 w-2.5 mr-1 transition-transform duration-150",
                    isAgentExpanded && "rotate-90",
                  )}
                />
                <span>
                  {isAgentExpanded ? "Hide" : "Pick"} specific agents
                  {pickedHere > 0 && (
                    <span className="ml-1 text-amber-600 font-medium">({pickedHere})</span>
                  )}
                </span>
              </button>
              {isAgentExpanded && (
                <div style={{ paddingLeft: `${28 + depth * 16}px` }}>
                  {isLoading ? (
                    <div className="text-[10.5px] text-gray-400 py-1">Loading agents…</div>
                  ) : !agents || agents.length === 0 ? (
                    <div className="text-[10.5px] text-gray-400 py-1">No agents in this sub-agency.</div>
                  ) : (
                    agents.map((agent) => {
                      const userChecked = (customSelectedUserIds || []).includes(agent.id);
                      return (
                        <button
                          key={agent.id}
                          type="button"
                          className={cn(
                            "flex items-center w-full text-left px-2 py-1 text-[11.5px] rounded-md transition-colors",
                            userChecked
                              ? "bg-amber-50 hover:bg-amber-100"
                              : "hover:bg-muted",
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleCustomUser?.(agent.id);
                          }}
                        >
                          <span
                            aria-hidden
                            className={cn(
                              "mr-2 h-3 w-3 flex-shrink-0 rounded-sm border flex items-center justify-center",
                              userChecked
                                ? "bg-amber-600 border-amber-600 text-white"
                                : "border-gray-300 bg-white",
                            )}
                          >
                            {userChecked && <Check className="h-2 w-2" strokeWidth={3} />}
                          </span>
                          <span className="truncate">{agent.name || agent.email || "Unnamed agent"}</span>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </>
          );
        })()
      )}

      {/* Children (expanded) */}
      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child) => (
            <AgencyTreeNode
              key={child.id}
              node={child}
              selectedId={selectedId}
              onSelect={onSelect}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
              depth={depth + 1}
              customMode={customMode}
              customSelectedIds={customSelectedIds}
              onToggleCustom={onToggleCustom}
              rootAgencyId={rootAgencyId}
              agentsByAgency={agentsByAgency}
              loadingAgentsAgencyId={loadingAgentsAgencyId}
              expandedAgentAgencies={expandedAgentAgencies}
              onToggleAgentExpand={onToggleAgentExpand}
              customSelectedUserIds={customSelectedUserIds}
              onToggleCustomUser={onToggleCustomUser}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export const AdvancedLeaderboardFilters = function({
  selectedPeriod,
  selectedMetric,
  searchQuery: parentSearchQuery = "", // Receive parent's search query
  onPeriodChange,
  onMetricChange,
  onSearch,
  onRefresh,
  loading = false,
  leaderboardData,
  customDateRange,
  onCustomDateChange,
  // Agency switcher props
  agencies,
  selectedAgencyId,
  onAgencyChange,
  showAgencySwitcher = false,
  // Scope picker props
  isAgencyOwner = false,
  ownerAgencyId,
  ownerAgencyName,
  onScopeSaved,
  parentGodmodeAvailable,
  isGodmodeActive,
  onGodmodeChange,
  onShare,
}: AdvancedLeaderboardFiltersProps) {
  const onDirect130 = useDirect130Host(); // no % to goal on Direct130's board (the column is hidden there)
  const [localSearchQuery, setLocalSearchQuery] = useState(parentSearchQuery);
  const [isAgencyOpen, setIsAgencyOpen] = useState(false);
  const [expandedAgencyIds, setExpandedAgencyIds] = useState<Set<string>>(new Set());
  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    customDateRange ? { from: customDateRange.from, to: customDateRange.to } : undefined
  );
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const { toast } = useToast();
  const presetRanges = useMemo(() => getPresetRanges(), []);

  // Agency tree for hierarchical dropdown
  const agencyTreeRoots = useMemo(() => buildAgencyTree(agencies || []), [agencies]);
  const selectedAgencyName = useMemo(() => {
    return agencies?.find((a) => a.id === selectedAgencyId)?.name || "Select agency";
  }, [agencies, selectedAgencyId]);

  // Scope state — only used when isAgencyOwner. Loaded from backend on mount,
  // persisted via PUT /agencies/:id/leaderboard-settings whenever the owner
  // changes mode or applies a custom selection.
  // Initial state matches the column default (agency_wide_enabled=false ⇒
  // "Just mine") so the wrong button doesn't briefly appear active during the
  // initial GET round-trip. The real value overrides this in useEffect below.
  const [scope, setScope] = useState<LeaderboardScope>({
    mode: "agency_only",
    customAgencyIds: [],
    customUserIds: [],
  });
  // Lazy-loaded agents per agency for the custom-mode expand (agency.id → agents)
  const [agentsByAgency, setAgentsByAgency] = useState<Record<string, AgencyAgent[]>>({});
  const [loadingAgentsAgencyId, setLoadingAgentsAgencyId] = useState<string | null>(null);
  const [expandedAgentAgencies, setExpandedAgentAgencies] = useState<Set<string>>(new Set());
  const [isScopeLoading, setIsScopeLoading] = useState(false);
  const [isScopeSaving, setIsScopeSaving] = useState(false);
  // Force Sub-Agency Inclusion — local mirror of the persisted setting.
  // Only shown to owners and only when Agency-Wide mode is selected.
  // Toggling this fires its own PUT to the leaderboard-settings endpoint;
  // doesn't go through persistScope because it's not part of the scope shape.
  const [forceSubAgencyInclusion, setForceSubAgencyInclusion] = useState(false);
  const [isForceSaving, setIsForceSaving] = useState(false);

  useEffect(() => {
    if (!isAgencyOwner || !ownerAgencyId) return;
    let cancelled = false;
    (async () => {
      try {
        setIsScopeLoading(true);
        const res = await handleApiCall<{
          leaderboardEnabled: boolean;
          agencyWideEnabled?: boolean;
          forceSubAgencyInclusion?: boolean;
          leaderboardScope?: {
            customAgencyIds?: string[];
            customUserIds?: string[];
          } | null;
        }>(async () =>
          await api.get({
            url: getApiUrl(endpoint.agency.getLeaderboardSettings(ownerAgencyId)),
            auth: true,
          }),
        );
        if (!cancelled) {
          const next = backendToUiMode(
            res?.agencyWideEnabled === true,
            Array.isArray(res?.leaderboardScope?.customAgencyIds)
              ? res!.leaderboardScope!.customAgencyIds!
              : [],
            Array.isArray(res?.leaderboardScope?.customUserIds)
              ? res!.leaderboardScope!.customUserIds!
              : [],
          );
          // If the page already says we're in a god-mode session, keep the
          // picker UI in god-mode — the page is the source of truth for the
          // session flag and we don't want to flicker it back to the
          // persisted mode on first paint.
          setScope(isGodmodeActive ? { ...next, mode: "parent_godmode" } : next);
          setForceSubAgencyInclusion(res?.forceSubAgencyInclusion === true);
        }
      } catch {
        // Defaults are fine.
      } finally {
        if (!cancelled) setIsScopeLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isAgencyOwner, ownerAgencyId]);

  // Persist force-sub-agency-inclusion toggle. Optimistic update with
  // revert on failure — same UX as the Settings / Agency Center surface,
  // just sized to inline.
  const persistForceToggle = useCallback(async (next: boolean) => {
    if (!ownerAgencyId) return;
    const prev = forceSubAgencyInclusion;
    setForceSubAgencyInclusion(next);
    setIsForceSaving(true);
    try {
      await handleApiCall(async () =>
        await api.put({
          url: getApiUrl(endpoint.agency.updateLeaderboardSettings(ownerAgencyId)),
          body: { forceSubAgencyInclusion: next },
          auth: true,
        }),
      );
      toast({
        title: next ? "Forcing sub-agencies on" : "Reverted to mutual consent",
        description: next
          ? "All sub-agencies below you are now in the Agency-Wide pool — even ones that haven't toggled it themselves."
          : "Sub-agencies have to toggle Agency-Wide on themselves to be included.",
      });
      onScopeSaved?.();
    } catch (err: any) {
      setForceSubAgencyInclusion(prev);
      toast({
        title: "Couldn't update",
        description: err?.response?.data?.message || "Try again.",
        variant: "destructive",
      });
    } finally {
      setIsForceSaving(false);
    }
  }, [forceSubAgencyInclusion, ownerAgencyId, toast, onScopeSaved]);

  // Lazy-fetch agents for an agency (only triggered when owner expands a row).
  // Endpoint returns AgencyUser memberships with actual user fields under
  // `.user`, not flat users. Map to {id: userId, name, email}.
  const loadAgencyAgents = useCallback(async (agencyId: string) => {
    if (agentsByAgency[agencyId]) return; // Already loaded
    try {
      setLoadingAgentsAgencyId(agencyId);
      const res = await handleApiCall<any>(async () =>
        await api.get({
          url: getApiUrl(endpoint.agency.getAgencyUsers(agencyId)),
          auth: true,
        }),
      );
      // Endpoint shape: array of agency_user memberships, each with `.user`
      // sub-object holding name/email/showOnLeaderboard. Older callers may
      // see { users: [...] } or { data: [...] }, handle defensively.
      const rawList: any[] = Array.isArray(res)
        ? res
        : Array.isArray(res?.users)
          ? res.users
          : Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res?.data?.users)
              ? res.data.users
              : [];

      const mapped: AgencyAgent[] = rawList
        .map((row) => {
          // Membership row → flatten to {id: userId, name, email, showOnLeaderboard}
          const u = row?.user || row;
          const userId = row?.userId || u?.id;
          if (!userId) return null;
          return {
            id: userId,
            name: u?.name ?? null,
            email: u?.email ?? null,
            showOnLeaderboard: u?.showOnLeaderboard,
          } as AgencyAgent;
        })
        .filter((u): u is AgencyAgent => !!u && u.showOnLeaderboard !== false);

      setAgentsByAgency((prev) => ({ ...prev, [agencyId]: mapped }));
    } catch {
      // Show empty state on failure
      setAgentsByAgency((prev) => ({ ...prev, [agencyId]: [] }));
    } finally {
      setLoadingAgentsAgencyId((cur) => (cur === agencyId ? null : cur));
    }
  }, [agentsByAgency]);

  const toggleAgentExpand = useCallback((agencyId: string) => {
    setExpandedAgentAgencies((prev) => {
      const next = new Set(prev);
      if (next.has(agencyId)) {
        next.delete(agencyId);
      } else {
        next.add(agencyId);
        void loadAgencyAgents(agencyId);
      }
      return next;
    });
  }, [loadAgencyAgents]);

  const toggleCustomUser = useCallback((userId: string) => {
    setScope((prev) => {
      if (prev.mode !== "custom") return prev;
      const exists = prev.customUserIds.includes(userId);
      return {
        ...prev,
        customUserIds: exists
          ? prev.customUserIds.filter((x) => x !== userId)
          : [...prev.customUserIds, userId],
      };
    });
  }, []);

  const persistScope = useCallback(async (next: LeaderboardScope) => {
    if (!ownerAgencyId) return;
    try {
      setIsScopeSaving(true);
      const body = uiModeToBackendBody(next);
      await handleApiCall(async () =>
        await api.put({
          url: getApiUrl(endpoint.agency.updateLeaderboardSettings(ownerAgencyId)),
          body,
          auth: true,
        }),
      );
      setScope(next);
      toast({
        title:
          next.mode === "agency_only"
            ? "Agency-Wide Mode disabled"
            : next.mode === "agency_wide"
              ? "Agency-Wide Mode enabled"
              : (() => {
                  const a = next.customAgencyIds.length;
                  const u = next.customUserIds.length;
                  const parts: string[] = [];
                  if (a > 0) parts.push(`${a} ${a === 1 ? "sub-agency" : "sub-agencies"}`);
                  if (u > 0) parts.push(`${u} ${u === 1 ? "agent" : "agents"}`);
                  return `Custom scope: ${parts.join(" + ")}`;
                })(),
        description:
          next.mode === "agency_only"
            ? "Leaderboard and team chat are now isolated to your agency only."
            : next.mode === "agency_wide"
              ? "Leaderboard and team chat will merge with sub-agencies that also have this enabled."
              : "Sub-agencies must also have Agency-Wide Mode on to appear here.",
      });
      onScopeSaved?.();
    } catch (err: any) {
      toast({
        title: "Couldn't save scope",
        description: err?.response?.data?.message || "Try again.",
        variant: "destructive",
      });
    } finally {
      setIsScopeSaving(false);
    }
  }, [ownerAgencyId, toast, onScopeSaved]);

  const setScopeMode = (mode: ScopeMode) => {
    if (mode === scope.mode && mode !== "parent_godmode") {
      // No-op for normal modes, but god-mode is session-only — allow re-firing.
      return;
    }
    if (mode === "parent_godmode") {
      // Session-only — don't write to the persisted agency setting. Just
      // flag the page to add ?godmode=true on subsequent leaderboard fetches.
      setScope((prev) => ({ ...prev, mode: "parent_godmode" }));
      onGodmodeChange?.(true);
      return;
    }
    // Leaving god-mode — disable the session flag before persisting the new mode.
    if (scope.mode === "parent_godmode") {
      onGodmodeChange?.(false);
    }
    if (mode === "custom") {
      // Enter custom mode locally — owner picks agencies/agents then Apply.
      setScope({ mode, customAgencyIds: scope.customAgencyIds, customUserIds: scope.customUserIds });
      return;
    }
    void persistScope({ mode, customAgencyIds: [], customUserIds: [] });
  };

  const toggleCustomAgency = (id: string) => {
    if (scope.mode !== "custom") return;
    const exists = scope.customAgencyIds.includes(id);
    setScope((prev) => {
      const nextAgencyIds = exists
        ? prev.customAgencyIds.filter((x) => x !== id)
        : [...prev.customAgencyIds, id];
      // When checking the whole agency, drop any individual-agent picks for
      // that same agency (whole-agency wins; redundant per-agent picks would
      // confuse the count). Loaded agents only — unloaded ones can't conflict.
      const agentsHere = (agentsByAgency[id] || []).map((a) => a.id);
      const nextUserIds = exists
        ? prev.customUserIds
        : prev.customUserIds.filter((uid) => !agentsHere.includes(uid));
      return { mode: "custom", customAgencyIds: nextAgencyIds, customUserIds: nextUserIds };
    });
  };

  const applyCustomScope = async () => {
    if (scope.customAgencyIds.length === 0 && scope.customUserIds.length === 0) {
      toast({
        title: "Pick at least one agency or agent",
        description: "Custom scope needs one or more sub-agencies or specific agents.",
        variant: "destructive",
      });
      return;
    }
    await persistScope(scope);
    setIsAgencyOpen(false);
  };

  // Auto-expand the path to root on first open
  useEffect(() => {
    if (agencies && agencies.length > 0 && expandedAgencyIds.size === 0) {
      // Expand all root-level agencies by default so tree is visible
      const roots = new Set<string>();
      for (const a of agencies) {
        if (a.depth === 0) roots.add(a.id);
      }
      setExpandedAgencyIds(roots);
    }
  }, [agencies, expandedAgencyIds.size]);

  const toggleAgencyExpand = useCallback((id: string) => {
    setExpandedAgencyIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleAgencySelect = useCallback((id: string) => {
    onAgencyChange?.(id);
    setIsAgencyOpen(false);
  }, [onAgencyChange]);

  // Sync local state with parent state
  useEffect(() => {
    setLocalSearchQuery(parentSearchQuery);
  }, [parentSearchQuery]);

  // Sync date range with prop — only when prop actually changes value
  // Avoid resetting local state while user is actively picking dates
  useEffect(() => {
    if (customDateRange) {
      setDateRange((prev) => {
        // Only update if dates actually changed (avoid re-render loops)
        if (prev?.from?.getTime() === customDateRange.from.getTime() && prev?.to?.getTime() === customDateRange.to.getTime()) {
          return prev;
        }
        return { from: customDateRange.from, to: customDateRange.to };
      });
    } else if (selectedPeriod !== "custom") {
      setDateRange(undefined);
    }
  }, [customDateRange, selectedPeriod]);

  const clearAllFilters = () => {
    setLocalSearchQuery("");
    onSearch?.("");
    if (selectedPeriod === "custom") {
      onPeriodChange("this-month");
      onCustomDateChange?.(undefined);
      setDateRange(undefined);
    }
  };

  // Sanitize CSV fields to prevent formula injection attacks
  // Prevents malicious payloads like =cmd|'/c calc.exe'!A1 from executing
  const sanitizeCSVField = (value: string | undefined): string => {
    const str = String(value || 'N/A');
    // Check if string starts with formula characters (=, +, -, @, |, %)
    if (/^[=+\-@|%]/.test(str)) {
      // Prefix with single quote to prevent formula execution in Excel/Sheets
      return `'${str}`;
    }
    return str;
  };

  const handleExport = () => {
    // DEMO SANDBOX: offer the trial instead of downloading the board.
    //
    // Unlike the Leads and Master exports there is no backend call to gate here — the CSV
    // is assembled from data already in the browser and saved with a blob URL, so the
    // block has to live at the click or it doesn't exist at all.
    //
    // Checked before the empty-data guard so the visitor gets the offer rather than
    // "No Data to Export", which would read as the feature being broken.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            'Exporting the leaderboard is disabled in the demo sandbox. Start a free trial to export your agency data.',
        }),
      );
      return;
    }

    try {
      if (!leaderboardData?.leaderboard || leaderboardData.leaderboard.length === 0) {
        toast({
          title: "No Data to Export",
          description: "There is no leaderboard data available to export.",
          variant: "destructive",
        });
        return;
      }

      // Create CSV content
      const headers = [
        "Rank",
        "Name",
        "Agency",
        "Sub-Agency",
        "Total Premium",
        "Total Policies",
        "Closing Ratio",
        "Leads Converted",
        "Appointments",
        "Calls Made",
        "Active Clients"
      ];

      const csvRows = [headers.join(",")];

      leaderboardData.leaderboard.forEach((agent: any, index: number) => {
        const row = [
          index + 1,
          `"${sanitizeCSVField(agent.name)}"`,
          `"${sanitizeCSVField(agent.agency?.name || 'No Agency')}"`,
          `"${sanitizeCSVField(agent.subAgency?.name)}"`,
          agent.totalPremium || 0,
          agent.totalPolicies || agent.totalSales || 0,
          `${(agent.conversionRate || 0).toFixed(1)}%`,
          agent.leadsConverted || 0,
          agent.appointments || 0,
          agent.callsMade || 0,
          agent.activeClients || 0
        ];
        csvRows.push(row.join(","));
      });

      const csvContent = csvRows.join("\n");

      // Create blob and download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      const periodLabel = selectedPeriod.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      // Format date in local timezone to avoid UTC shift
      const now = new Date();
      const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const fileName = `leaderboard-${selectedPeriod}-${timestamp}.csv`;

      link.setAttribute("href", url);
      link.setAttribute("download", fileName);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "Export Successful",
        description: `Leaderboard data exported as ${fileName}`,
      });
    } catch (error) {

      toast({
        title: "Export Failed",
        description: "Failed to export leaderboard data. Please try again.",
        variant: "destructive",
      });
    }
  };

  const hasActiveFilters = localSearchQuery.trim().length > 0 || selectedPeriod === "custom";

  const handleSearchChange = (value: string) => {
    setLocalSearchQuery(value);
    onSearch?.(value);
  };

  const handlePeriodChange = (value: string) => {
    onPeriodChange(value);
    // Clear custom date range when switching away from custom
    if (value !== "custom") {
      onCustomDateChange?.(undefined);
      setDateRange(undefined);
    }
  };

  const formatDateRange = (range: DateRange | undefined) => {
    if (!range?.from) return "Select dates";
    if (!range.to) return format(range.from, "MMM d, yyyy");
    // Same day
    if (range.from.toDateString() === range.to.toDateString()) {
      return format(range.from, "MMM d, yyyy");
    }
    // Same month & year
    if (range.from.getMonth() === range.to.getMonth() && range.from.getFullYear() === range.to.getFullYear()) {
      return `${format(range.from, "MMM d")} - ${format(range.to, "d, yyyy")}`;
    }
    // Same year
    if (range.from.getFullYear() === range.to.getFullYear()) {
      return `${format(range.from, "MMM d")} - ${format(range.to, "MMM d, yyyy")}`;
    }
    // Different years
    return `${format(range.from, "MMM d, yyyy")} - ${format(range.to, "MMM d, yyyy")}`;
  };

  // Calculate days selected for display
  const daysSelected = useMemo(() => {
    if (!dateRange?.from || !dateRange?.to) return null;
    const days = differenceInDays(dateRange.to, dateRange.from) + 1;
    return days === 1 ? "1 day" : `${days} days`;
  }, [dateRange]);

  const handleDateSelect = (range: DateRange | undefined) => {
    setDateRange(range);
    // Only notify parent when we have both from and to dates
    if (range?.from && range?.to && onCustomDateChange) {
      onCustomDateChange({ from: range.from, to: range.to });
      // Close the calendar after selecting the range
      setIsCalendarOpen(false);
    }
  };

  const handlePresetSelect = (preset: { from: Date; to: Date }) => {
    const range = { from: preset.from, to: preset.to };
    setDateRange(range);
    if (onCustomDateChange) {
      onCustomDateChange(range);
    }
    setIsCalendarOpen(false);
  };

  const handleClearRange = () => {
    setDateRange(undefined);
    if (onCustomDateChange) {
      onCustomDateChange(undefined);
    }
  };

  return (
    <Card className="border-2 border-gray-200" data-tour="leaderboard-filters">
      <CardContent className="pt-6">
        <div className="space-y-6">
          {/* Primary Filters Row */}
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Agency Switcher - expandable tree for agency owners/admins with sub-agencies */}
            {showAgencySwitcher && agencies && agencies.length > 1 && (
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <label
                    className="text-sm font-medium text-gray-700"
                    htmlFor="agency-select"
                  >
                    Agency
                  </label>
                  {isAgencyOwner && (
                    <TooltipProvider delayDuration={150}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={() => setIsAgencyOpen(true)}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 hover:border-amber-300 text-[11px] font-medium transition-colors"
                          >
                            <Trophy className="h-3 w-3" />
                            Set scope
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs text-xs">
                          <p className="font-semibold mb-1">Set Leaderboard Scope</p>
                          <p className="text-muted-foreground leading-relaxed">
                            Choose who appears on your leaderboard:
                            <br />• <span className="font-medium">Just mine</span> — only your agency
                            <br />• <span className="font-medium">Agency-wide</span> — every consenting sub-agency
                            <br />• <span className="font-medium">Custom</span> — pick specific sub-agencies or agents
                          </p>
                          <p className="mt-1.5 text-[10px] text-muted-foreground italic">
                            Both your agency and the sub-agency must opt in.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                <Popover open={isAgencyOpen} onOpenChange={setIsAgencyOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={isAgencyOpen}
                      title={isAgencyOwner ? "Switch agency or set leaderboard scope" : "Switch agency"}
                      className={cn(
                        "w-full justify-between font-normal",
                        // Owner-only subtle amber accent so the dropdown reads
                        // as "this is where scope controls live" without an
                        // explicit text label cluttering the layout.
                        isAgencyOwner && "border-amber-200 bg-amber-50/40 hover:bg-amber-50",
                      )}
                    >
                      <span className="flex items-center truncate">
                        <Building2 className="h-4 w-4 mr-2 flex-shrink-0 text-muted-foreground" />
                        <span className="truncate">{selectedAgencyName}</span>
                      </span>
                      <span className="flex items-center gap-1 ml-2 flex-shrink-0">
                        {isAgencyOwner && (
                          <Trophy className="h-3.5 w-3.5 text-amber-500" />
                        )}
                        <ChevronDown className={cn(
                          "h-4 w-4 text-muted-foreground transition-transform duration-200",
                          isAgencyOpen && "rotate-180"
                        )} />
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-[360px] p-0" sideOffset={4}>
                    {/* Owner-only scope picker. Three modes: just my agency,
                        agency-wide, or custom (multi-select sub-agencies).
                        Mutual opt-in: a sub-agency only appears when both this
                        agency AND the sub-agency have picked agency_wide. */}
                    {isAgencyOwner && (
                      <div className="border-b">
                        <div className="px-3 pt-3 pb-2 flex items-center gap-2">
                          <Trophy className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" />
                          <p className="text-xs font-semibold text-gray-900">Leaderboard Scope</p>
                          <Badge variant="secondary" className="text-[10px] font-medium bg-gray-100 text-gray-700">
                            Owner
                          </Badge>
                          {scope.mode === "parent_godmode" && (
                            <Badge variant="secondary" className="text-[10px] font-medium bg-violet-100 text-violet-800 ml-1">
                              Owner view
                            </Badge>
                          )}
                          {(isScopeLoading || isScopeSaving) && (
                            <Loader2 className="h-3 w-3 ml-auto animate-spin text-gray-400" />
                          )}
                        </div>
                        {/* 4-button row was too tight in the 360px popover —
                            "Full downline" got truncated. Switching to a 2×2
                            grid when god-mode is available gives each button
                            ~half-width (~150px), which fits the longest label
                            cleanly. The 3-mode (no god-mode) case stays as a
                            single row since 3 fit comfortably. */}
                        <div className={parentGodmodeAvailable ? "px-3 pb-2 grid grid-cols-2 gap-1.5" : "px-3 pb-2 grid grid-cols-3 gap-1.5"}>
                          <ScopeModeButton
                            label="Just mine"
                            icon={Users}
                            active={scope.mode === "agency_only"}
                            disabled={isScopeSaving}
                            onClick={() => setScopeMode("agency_only")}
                          />
                          <ScopeModeButton
                            label="Agency-wide"
                            icon={Globe}
                            active={scope.mode === "agency_wide"}
                            disabled={isScopeSaving}
                            onClick={() => setScopeMode("agency_wide")}
                          />
                          <ScopeModeButton
                            label="Custom"
                            icon={Building2}
                            active={scope.mode === "custom"}
                            disabled={isScopeSaving}
                            onClick={() => setScopeMode("custom")}
                          />
                          {parentGodmodeAvailable && (
                            <ScopeModeButton
                              label="Full downline"
                              icon={Eye}
                              active={scope.mode === "parent_godmode"}
                              disabled={isScopeSaving}
                              onClick={() => setScopeMode("parent_godmode")}
                            />
                          )}
                        </div>
                        {(scope.mode === "agency_wide" || scope.mode === "custom") && (
                          <>
                            <p className="px-3 pb-2 text-[11px] text-gray-500 leading-relaxed">
                              Wired to <span className="font-semibold text-gray-700">Agency-Wide Mode</span> in Agency Center. Sub-agencies must also turn this on for it to work — both sides have to opt in. Affects leaderboard <span className="font-semibold text-gray-700">and</span> team chat.
                            </p>
                            {/* Force Sub-Agency Inclusion — inline mirror of the
                                same toggle in Agency Center + Settings. Lets the
                                owner force all sub-agencies into the pool from
                                here without leaving the leaderboard. Only the
                                owner sees this (gated by isAgencyOwner higher up). */}
                            <div className="mx-3 mb-3 rounded-md border border-orange-200 bg-orange-50 p-2.5 flex items-start gap-2.5">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[11px] font-semibold text-orange-900">Force-include sub-agencies</span>
                                  {forceSubAgencyInclusion && (
                                    <Badge variant="secondary" className="bg-orange-100 text-orange-800 text-[9px] font-medium h-4 px-1">Forcing</Badge>
                                  )}
                                </div>
                                <p className="text-[10px] text-orange-700 mt-0.5 leading-snug">
                                  {forceSubAgencyInclusion
                                    ? "Every sub-agency is in — even ones that didn't toggle. Public to all agents."
                                    : "Off — sub-agencies have to toggle Agency-Wide themselves."}
                                </p>
                              </div>
                              <div className="shrink-0 pt-0.5">
                                {isForceSaving ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin text-orange-400" />
                                ) : (
                                  <Switch
                                    checked={forceSubAgencyInclusion}
                                    onCheckedChange={persistForceToggle}
                                    disabled={isScopeSaving}
                                    className="data-[state=checked]:bg-orange-600 scale-90"
                                  />
                                )}
                              </div>
                            </div>
                          </>
                        )}
                        {scope.mode === "parent_godmode" && (
                          <div className="px-3 pb-3 text-[11px] leading-relaxed bg-violet-50 border-t border-violet-200">
                            <p className="pt-2 text-violet-900">
                              <span className="font-semibold">Full downline view.</span> You see every sub-agency below you — even ones that haven't opted into shared leaderboards — and every agent including those who hid themselves. <span className="font-semibold">Visible only to you.</span> Sub-agencies don't see this view.
                            </p>
                            <p className="pt-1 text-violet-700">
                              Logged for audit. Switch back to a normal scope when done.
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="max-h-[340px] overflow-y-auto p-1">
                      {agencyTreeRoots.map((root) => (
                        <AgencyTreeNode
                          key={root.id}
                          node={root}
                          selectedId={selectedAgencyId}
                          onSelect={handleAgencySelect}
                          expandedIds={expandedAgencyIds}
                          onToggleExpand={toggleAgencyExpand}
                          // Custom-mode multi-select overlay (owner only).
                          customMode={isAgencyOwner && scope.mode === "custom"}
                          customSelectedIds={scope.customAgencyIds}
                          onToggleCustom={toggleCustomAgency}
                          rootAgencyId={ownerAgencyId}
                          // Per-agent expansion (custom mode only).
                          agentsByAgency={agentsByAgency}
                          loadingAgentsAgencyId={loadingAgentsAgencyId}
                          expandedAgentAgencies={expandedAgentAgencies}
                          onToggleAgentExpand={toggleAgentExpand}
                          customSelectedUserIds={scope.customUserIds}
                          onToggleCustomUser={toggleCustomUser}
                        />
                      ))}
                    </div>

                    {isAgencyOwner && scope.mode === "custom" && (
                      <div className="border-t p-3 flex items-center justify-between gap-2">
                        <span className="text-xs text-gray-500">
                          {(() => {
                            const a = scope.customAgencyIds.length;
                            const u = scope.customUserIds.length;
                            if (a === 0 && u === 0) return "Nothing selected yet";
                            const parts: string[] = [];
                            if (a > 0) parts.push(`${a} ${a === 1 ? "agency" : "agencies"}`);
                            if (u > 0) parts.push(`${u} ${u === 1 ? "agent" : "agents"}`);
                            return parts.join(" + ");
                          })()}
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          className="bg-amber-600 hover:bg-amber-700 h-8"
                          onClick={applyCustomScope}
                          disabled={
                            isScopeSaving ||
                            (scope.customAgencyIds.length === 0 && scope.customUserIds.length === 0)
                          }
                        >
                          {isScopeSaving ? "Saving..." : "Apply"}
                        </Button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              </div>
            )}

            <div className="flex-1">
              <label
                className="text-sm font-medium text-gray-700 mb-2 block"
                htmlFor="time-period"
              >
                Time Period
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <Select onValueChange={handlePeriodChange} value={selectedPeriod}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="this-week">This Week</SelectItem>
                    <SelectItem value="this-month">This Month</SelectItem>
                    <SelectItem value="last-month">Last Month</SelectItem>
                    <SelectItem value="this-quarter">This Quarter</SelectItem>
                    <SelectItem value="this-year">This Year</SelectItem>
                    <SelectItem value="custom">Custom Range</SelectItem>
                  </SelectContent>
                </Select>

                {/* Custom date picker - only show when "Custom Range" is selected */}
                {selectedPeriod === "custom" && (
                  <Popover onOpenChange={setIsCalendarOpen} open={isCalendarOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        className={cn(
                          "w-full sm:w-auto justify-start text-left font-normal",
                          !dateRange && "text-muted-foreground"
                        )}
                        variant="outline"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{formatDateRange(dateRange)}</span>
                        {daysSelected && (
                          <span className="ml-2 text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded hidden sm:inline">
                            {daysSelected}
                          </span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-auto p-0" sideOffset={4}>
                      <div className="flex flex-col sm:flex-row">
                        {/* Preset shortcuts sidebar with grouped sections */}
                        <div className="border-b sm:border-b-0 sm:border-r p-2 sm:w-48 flex-shrink-0 max-h-[400px] overflow-y-auto">
                          {/* Quick presets */}
                          <div className="text-xs font-semibold text-muted-foreground px-2 py-1.5 uppercase tracking-wide">Quick</div>
                          <div className="flex flex-wrap sm:flex-col gap-0.5 mb-2">
                            {presetRanges.filter(p => p.group === 'quick').map((preset) => (
                              <Button
                                key={preset.label}
                                variant="ghost"
                                size="sm"
                                className={cn(
                                  "justify-start text-xs h-7 px-2 font-normal",
                                  dateRange?.from?.toDateString() === preset.from.toDateString() &&
                                  dateRange?.to?.toDateString() === preset.to.toDateString() &&
                                  "bg-primary/10 text-primary"
                                )}
                                onClick={() => handlePresetSelect(preset)}
                              >
                                {preset.label}
                              </Button>
                            ))}
                          </div>

                          {/* This Week presets */}
                          <div className="text-xs font-semibold text-muted-foreground px-2 py-1.5 uppercase tracking-wide border-t pt-2">This Week</div>
                          <div className="flex flex-wrap sm:flex-col gap-0.5 mb-2">
                            {presetRanges.filter(p => p.group === 'this-week').map((preset) => (
                              <Button
                                key={preset.label}
                                variant="ghost"
                                size="sm"
                                className={cn(
                                  "justify-start text-xs h-7 px-2 font-normal",
                                  dateRange?.from?.toDateString() === preset.from.toDateString() &&
                                  dateRange?.to?.toDateString() === preset.to.toDateString() &&
                                  "bg-primary/10 text-primary"
                                )}
                                onClick={() => handlePresetSelect(preset)}
                              >
                                {preset.label}
                              </Button>
                            ))}
                          </div>

                          {/* Last Week presets */}
                          <div className="text-xs font-semibold text-muted-foreground px-2 py-1.5 uppercase tracking-wide border-t pt-2">Last Week</div>
                          <div className="flex flex-wrap sm:flex-col gap-0.5 mb-2">
                            {presetRanges.filter(p => p.group === 'last-week').map((preset) => (
                              <Button
                                key={preset.label}
                                variant="ghost"
                                size="sm"
                                className={cn(
                                  "justify-start text-xs h-7 px-2 font-normal",
                                  dateRange?.from?.toDateString() === preset.from.toDateString() &&
                                  dateRange?.to?.toDateString() === preset.to.toDateString() &&
                                  "bg-primary/10 text-primary"
                                )}
                                onClick={() => handlePresetSelect(preset)}
                              >
                                {preset.label}
                              </Button>
                            ))}
                          </div>

                          {/* Monthly presets */}
                          <div className="text-xs font-semibold text-muted-foreground px-2 py-1.5 uppercase tracking-wide border-t pt-2">Monthly</div>
                          <div className="flex flex-wrap sm:flex-col gap-0.5">
                            {presetRanges.filter(p => p.group === 'monthly').map((preset) => (
                              <Button
                                key={preset.label}
                                variant="ghost"
                                size="sm"
                                className={cn(
                                  "justify-start text-xs h-7 px-2 font-normal",
                                  dateRange?.from?.toDateString() === preset.from.toDateString() &&
                                  dateRange?.to?.toDateString() === preset.to.toDateString() &&
                                  "bg-primary/10 text-primary"
                                )}
                                onClick={() => handlePresetSelect(preset)}
                              >
                                {preset.label}
                              </Button>
                            ))}
                          </div>
                        </div>

                        {/* Calendar */}
                        <div className="p-2">
                          <Calendar
                            defaultMonth={dateRange?.from || new Date()}
                            mode="range"
                            numberOfMonths={2}
                            onSelect={handleDateSelect}
                            selected={dateRange}
                            className="[&_.rdp-months]:flex-col sm:[&_.rdp-months]:flex-row [&_.rdp-month]:w-full"
                            showOutsideDays={false}
                          />

                          {/* Footer with selected range info and actions */}
                          <div className="border-t mt-2 pt-2 px-1 flex items-center justify-between">
                            <div className="text-xs text-muted-foreground">
                              {dateRange?.from && dateRange?.to ? (
                                <span className="flex items-center gap-1">
                                  <span>{format(dateRange.from, "MMM d, yyyy")}</span>
                                  <ChevronRight className="h-3 w-3" />
                                  <span>{format(dateRange.to, "MMM d, yyyy")}</span>
                                  {daysSelected && <span className="text-primary font-medium ml-1">({daysSelected})</span>}
                                </span>
                              ) : dateRange?.from ? (
                                <span>Select end date</span>
                              ) : (
                                <span>Select start date</span>
                              )}
                            </div>
                            {dateRange && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                                onClick={handleClearRange}
                              >
                                <X className="h-3 w-3 mr-1" />
                                Clear
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>

            <div className="flex-1">
              <label
                className="text-sm font-medium text-gray-700 mb-2 block"
                htmlFor="primary-metric"
              >
                Primary Metric
              </label>
              <Select onValueChange={onMetricChange} value={selectedMetric}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="total-premium-sold">Total Premium Sold</SelectItem>
                  <SelectItem value="total-premium">Total Production</SelectItem>
                  <SelectItem value="total-policies">Total Policies</SelectItem>
                  <SelectItem value="conversion-rate">Closing Ratio</SelectItem>
                  <SelectItem value="appointments">Appointments</SelectItem>
                  <SelectItem value="calls-made">Calls Made</SelectItem>
                  {!onDirect130 && <SelectItem value="goal-progress">% to Goal</SelectItem>}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1">
              <label
                className="text-sm font-medium text-gray-700 mb-2 block"
                htmlFor="search-agents"
              >
                Search Agents
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-10"
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search by name..."
                  value={localSearchQuery}
                />
              </div>
            </div>
          </div>

          {/* Action Buttons Row */}
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="flex items-center gap-2">
              {!!hasActiveFilters && (
                <Button
                  className="text-muted-foreground hover:text-foreground"
                  onClick={clearAllFilters}
                  size="sm"
                  variant="ghost"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear Filters
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* Refresh button moved to the page-level header (LeaderboardFreshness)
                  where the "Last updated: Xs ago" timestamp lives. Keeping it here
                  created a duplicate that bypassed the backend cache cooldown UX. */}
              {!!onShare && (
                <Button onClick={onShare} size="sm" variant="outline">
                  <Share2 className="h-4 w-4 mr-1" />
                  Share
                </Button>
              )}
              <Button onClick={handleExport} size="sm" variant="outline">
                <Download className="h-4 w-4 mr-1" />
                Export
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

function ScopeModeButton({
  label,
  icon: Icon,
  active,
  disabled,
  onClick,
}: {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant={active ? "default" : "outline"}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        // Tight padding + smaller gap so the longest label ("Agency-wide")
        // fits without truncation in the 3-column grid.
        "h-8 gap-1 text-[11px] px-1.5 min-w-0",
        active ? "bg-amber-600 hover:bg-amber-700" : "",
      )}
    >
      <Icon className="h-3 w-3 flex-shrink-0" />
      <span className="whitespace-nowrap">{label}</span>
    </Button>
  );
}
